// AI Gateway Integration for Enhanced Payment Automation
export interface AIExtractionResult {
  amount: number;
  currency: string;
  recipient: string;
  reference: string;
  paymentMethod: string;
  confidence: number;
  processingTime: number;
}

export interface PaymentRoutingResult {
  transaction: {
    amount: number;
    currency: string;
  };
  recommendation: {
    recommended_provider: string;
    estimated_fee: number;
    processing_time: string;
    success_probability: string;
    reason: string;
  };
  model: string;
  timestamp: number;
}

export interface FraudDetectionResult {
  success: boolean;
  data: {
    results: {
      phone?: string[];
      email?: string[];
      patterns?: string[];
    };
    totalFound: number;
    processingTime: number;
    textStats: {
      wordCount: number;
      charCount: number;
      lineCount: number;
    };
    timestamp: string;
  };
  riskScore: number;
  riskLevel: "low" | "medium" | "high";
}

export interface AIGatewayMetrics {
  totalRequests: number;
  successfulRequests: number;
  averageResponseTime: number;
  modelUsage: { [model: string]: number };
  errorTypes: { [error: string]: number };
}

export class AIGatewayService {
  private baseURL = "https://emerald-neon-resonance-api.sv9.workers.dev";
  private metrics: AIGatewayMetrics = {
    totalRequests: 0,
    successfulRequests: 0,
    averageResponseTime: 0,
    modelUsage: {},
    errorTypes: {},
  };

  // Enhanced Payment Extraction using AI
  async extractPaymentDetails(
    text: string,
    language: string = "eng+tha",
  ): Promise<AIExtractionResult> {
    const startTime = Date.now();

    try {
      this.metrics.totalRequests++;

      const response = await fetch(`${this.baseURL}/api/ai/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: [
            {
              role: "user",
              content: `Extract payment details from this text and return JSON: ${text}. 
              Format: {"amount": number, "currency": string, "recipient": string, "reference": string, "paymentMethod": string}`,
            },
          ],
          model: "@cf/meta/llama-3-8b-instruct",
        }),
      });

      if (!response.ok) {
        throw new Error(`AI Gateway error: ${response.status}`);
      }

      const result = await response.json();
      const processingTime = Date.now() - startTime;

      // Track model usage
      this.metrics.modelUsage[result.model] =
        (this.metrics.modelUsage[result.model] || 0) + 1;
      this.metrics.successfulRequests++;

      // Update average response time
      this.updateAverageResponseTime(processingTime);

      // Parse the AI response
      let extractedData;
      try {
        extractedData = JSON.parse(
          result.response.replace(/```json\n?|\n?```/g, ""),
        );
      } catch (parseError) {
        // Fallback extraction using regex
        extractedData = this.fallbackExtraction(text);
      }

      return {
        ...extractedData,
        confidence: 0.85, // Default confidence for AI extraction
        processingTime,
      };
    } catch (error) {
      const err = error as Error;
      this.metrics.errorTypes[err.message] =
        (this.metrics.errorTypes[err.message] || 0) + 1;
      throw err;
    }
  }

  // Payment Routing using AI
  async routePayment(
    amount: number,
    currency: string,
    recipient: string,
    paymentType: string,
  ): Promise<PaymentRoutingResult> {
    const startTime = Date.now();

    try {
      this.metrics.totalRequests++;

      const response = await fetch(`${this.baseURL}/api/ai/payment-router`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount,
          currency,
          recipient,
          payment_type: paymentType,
        }),
      });

      if (!response.ok) {
        throw new Error(`Payment routing error: ${response.status}`);
      }

      const result = await response.json();
      const processingTime = Date.now() - startTime;

      this.metrics.modelUsage[result.model] =
        (this.metrics.modelUsage[result.model] || 0) + 1;
      this.metrics.successfulRequests++;
      this.updateAverageResponseTime(processingTime);

      return result;
    } catch (error) {
      const err = error as Error;
      this.metrics.errorTypes[err.message] =
        (this.metrics.errorTypes[err.message] || 0) + 1;
      throw err;
    }
  }

  // Fraud Detection using AI
  async detectFraud(
    text: string,
    pattern: string = "payment-fraud",
  ): Promise<FraudDetectionResult> {
    const startTime = Date.now();

    try {
      this.metrics.totalRequests++;

      const response = await fetch(`${this.baseURL}/api/pattern/quick-scan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text,
          pattern,
        }),
      });

      if (!response.ok) {
        throw new Error(`Fraud detection error: ${response.status}`);
      }

      const result = await response.json();
      const processingTime = Date.now() - startTime;

      this.metrics.successfulRequests++;
      this.updateAverageResponseTime(processingTime);

      // Calculate risk score based on patterns found
      const riskScore = this.calculateRiskScore(result.data);
      const riskLevel = this.determineRiskLevel(riskScore);

      return {
        ...result,
        riskScore,
        riskLevel,
      };
    } catch (error) {
      const err = error as Error;
      this.metrics.errorTypes[err.message] =
        (this.metrics.errorTypes[err.message] || 0) + 1;
      throw err;
    }
  }

  // Invoice Extraction (for future use with image processing)
  async extractFromInvoice(
    imageBase64: string,
    language: string = "eng+tha",
  ): Promise<AIExtractionResult> {
    const startTime = Date.now();

    try {
      this.metrics.totalRequests++;

      // This would require multipart form data - for now using text extraction
      const response = await fetch(`${this.baseURL}/api/invoices/extract`, {
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
        },
        body: imageBase64, // Simplified for demo
      });

      if (!response.ok) {
        throw new Error(`Invoice extraction error: ${response.status}`);
      }

      const result = await response.json();
      const processingTime = Date.now() - startTime;

      this.metrics.successfulRequests++;
      this.updateAverageResponseTime(processingTime);

      return result;
    } catch (error) {
      const err = error as Error;
      this.metrics.errorTypes[err.message] =
        (this.metrics.errorTypes[err.message] || 0) + 1;
      throw err;
    }
  }

  // Health Check
  async checkHealth(): Promise<{ status: string; performance: any }> {
    try {
      const response = await fetch(`${this.baseURL}/health`);
      return await response.json();
    } catch (error) {
      const err = error as Error;
      throw new Error(`Health check failed: ${err.message}`);
    }
  }

  // Get Metrics
  getMetrics(): AIGatewayMetrics {
    return { ...this.metrics };
  }

  // Private helper methods
  private fallbackExtraction(text: string): Partial<AIExtractionResult> {
    const amountMatch = text.match(/([\d,]+\.?\d*)\s*(THB|Baht| Bath|  Bath)/i);
    const phoneMatch = text.match(/0\d{8,9}|66\d{8,9}/g);
    const refMatch = text.match(/REF|Reference|ref[:\s]*([A-Z0-9-]+)/gi);
    const promptPayMatch = text.match(/promptpay|prompt pay/i);

    return {
      amount: amountMatch ? parseFloat(amountMatch[1].replace(/,/g, "")) : 0,
      currency: "THB",
      recipient: phoneMatch ? phoneMatch[0] : "",
      reference: refMatch ? refMatch[0].match(/([A-Z0-9-]+)/)?.[1] || "" : "",
      paymentMethod: promptPayMatch ? "PromptPay" : "Unknown",
    };
  }

  private calculateRiskScore(data: any): number {
    let score = 0;

    // Risk factors
    if (data.results.phone && data.results.phone.length > 0) score += 20;
    if (data.totalFound > 3) score += 30;
    if (data.textStats.wordCount < 5) score += 15;

    return Math.min(score, 100);
  }

  private determineRiskLevel(score: number): "low" | "medium" | "high" {
    if (score < 30) return "low";
    if (score < 70) return "medium";
    return "high";
  }

  private updateAverageResponseTime(newTime: number): void {
    if (this.metrics.successfulRequests <= 1) {
      this.metrics.averageResponseTime = newTime;
    } else {
      const total =
        this.metrics.averageResponseTime *
        (this.metrics.successfulRequests - 1);
      this.metrics.averageResponseTime =
        (total + newTime) / this.metrics.successfulRequests;
    }
  }

  // Reset metrics
  resetMetrics(): void {
    this.metrics = {
      totalRequests: 0,
      successfulRequests: 0,
      averageResponseTime: 0,
      modelUsage: {},
      errorTypes: {},
    };
  }
}

// Singleton instance
export const aiGateway = new AIGatewayService();
